import { useCallback, useState } from 'react';
import type {
  MedalCategory,
  MedalMaterial,
  MedalProduct,
  MedalProductDimension,
  MedalRibbonType,
  MedalRibbonStyle,
  MedalRibbonColor,
  MedalSetting,
  MedalQuote,
  MedalQuoteCustomItem,
  MedalActiveTab,
} from '../types/medals';
import * as medalsService from '../services/medals.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseMedalsProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  externalActiveTab?: MedalActiveTab;
}

export function useMedals({ accessToken, refreshToken, attemptRefresh, externalActiveTab }: UseMedalsProps) {
  // Active tab (use external if provided, otherwise internal)
  const [internalActiveTab, setInternalActiveTab] = useState<MedalActiveTab>('products');
  const activeTab = externalActiveTab ?? internalActiveTab;
  const setActiveTab = setInternalActiveTab;

  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Data states
  const [categories, setCategories] = useState<MedalCategory[]>([]);
  const [materials, setMaterials] = useState<MedalMaterial[]>([]);
  const [products, setProducts] = useState<MedalProduct[]>([]);
  const [ribbonTypes, setRibbonTypes] = useState<MedalRibbonType[]>([]);
  const [ribbonStyles, setRibbonStyles] = useState<MedalRibbonStyle[]>([]);
  const [ribbonColors, setRibbonColors] = useState<MedalRibbonColor[]>([]);
  const [settings, setSettings] = useState<MedalSetting[]>([]);
  const [quotes, setQuotes] = useState<MedalQuote[]>([]);
  const [pendingCustomItems, setPendingCustomItems] = useState<MedalQuoteCustomItem[]>([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<unknown>(null);

  // ===== Fetch Functions =====

  const fetchCategories = useCallback(async () => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => medalsService.fetchCategories(accessToken!, includeInactive),
        refreshToken,
        attemptRefresh
      );
      setCategories(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar categorias' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, includeInactive]);

  const fetchMaterials = useCallback(async () => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => medalsService.fetchMaterials(accessToken!, { search: searchTerm, includeInactive }),
        refreshToken,
        attemptRefresh
      );
      setMaterials(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar materiais' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, searchTerm, includeInactive]);

  const fetchProducts = useCallback(async (categoryId?: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => medalsService.fetchProducts(accessToken!, { search: searchTerm, categoryId, includeInactive }),
        refreshToken,
        attemptRefresh
      );
      setProducts(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar produtos' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, searchTerm, includeInactive]);

  const fetchRibbons = useCallback(async () => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const [types, styles, colors] = await withAuthRetry(
        () => Promise.all([
          medalsService.fetchRibbonTypes(accessToken!, includeInactive),
          medalsService.fetchRibbonStyles(accessToken!, includeInactive),
          medalsService.fetchRibbonColors(accessToken!, includeInactive),
        ]),
        refreshToken,
        attemptRefresh
      );
      setRibbonTypes(types);
      setRibbonStyles(styles);
      setRibbonColors(colors);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar fitas' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, includeInactive]);

  const fetchSettings = useCallback(async () => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => medalsService.fetchSettings(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setSettings(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar configurações' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  const fetchQuotes = useCallback(async (params?: { search?: string; status?: string }) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => medalsService.fetchQuotes(accessToken!, params),
        refreshToken,
        attemptRefresh
      );
      setQuotes(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar orçamentos' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  const fetchPendingCustomItems = useCallback(async () => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      const data = await medalsService.fetchPendingCustomItems(accessToken);
      setPendingCustomItems(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar itens pendentes' };
    }
  }, [accessToken]);

  // ===== Fetch All Based on Active Tab =====

  const fetchData = useCallback(async () => {
    switch (activeTab) {
      case 'categories':
        return fetchCategories();
      case 'materials':
        return fetchMaterials();
      case 'products':
        // Fetch categories for dropdown too
        await fetchCategories();
        await fetchMaterials();
        return fetchProducts();
      case 'ribbons':
        return fetchRibbons();
      case 'settings':
        return fetchSettings();
      case 'quotes':
        await fetchMaterials();
        await fetchProducts();
        await fetchPendingCustomItems();
        return fetchQuotes();
      default:
        return { success: true };
    }
  }, [activeTab, fetchCategories, fetchMaterials, fetchProducts, fetchRibbons, fetchSettings, fetchQuotes, fetchPendingCustomItems]);

  // ===== CRUD Operations =====

  const createCategory = useCallback(async (data: Parameters<typeof medalsService.createCategory>[0]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      await medalsService.createCategory(data, accessToken);
      await fetchCategories();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao criar categoria' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchCategories]);

  const updateCategory = useCallback(async (id: string, data: Parameters<typeof medalsService.updateCategory>[1]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      await medalsService.updateCategory(id, data, accessToken);
      await fetchCategories();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar categoria' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchCategories]);

  const deleteCategory = useCallback(async (id: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await medalsService.deleteCategory(id, accessToken);
      await fetchCategories();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir categoria' };
    }
  }, [accessToken, fetchCategories]);

  const createMaterial = useCallback(async (data: Parameters<typeof medalsService.createMaterial>[0]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      await medalsService.createMaterial(data, accessToken);
      await fetchMaterials();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao criar material' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchMaterials]);

  const updateMaterial = useCallback(async (id: string, data: Parameters<typeof medalsService.updateMaterial>[1]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      await medalsService.updateMaterial(id, data, accessToken);
      await fetchMaterials();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar material' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchMaterials]);

  const deleteMaterial = useCallback(async (id: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await medalsService.deleteMaterial(id, accessToken);
      await fetchMaterials();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir material' };
    }
  }, [accessToken, fetchMaterials]);

  const createProduct = useCallback(async (data: Parameters<typeof medalsService.createProduct>[0]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      const product = await medalsService.createProduct(data, accessToken);
      await fetchProducts();
      return { success: true, data: product };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao criar produto' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchProducts]);

  const updateProduct = useCallback(async (id: string, data: Parameters<typeof medalsService.updateProduct>[1]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      const product = await medalsService.updateProduct(id, data, accessToken);
      await fetchProducts();
      return { success: true, data: product };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar produto' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchProducts]);

  const deleteProduct = useCallback(async (id: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await medalsService.deleteProduct(id, accessToken);
      await fetchProducts();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir produto' };
    }
  }, [accessToken, fetchProducts]);

  const toggleProductStatus = useCallback(async (id: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await medalsService.toggleProductStatus(id, accessToken);
      await fetchProducts();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao alternar status' };
    }
  }, [accessToken, fetchProducts]);

  const updateQuoteStatus = useCallback(async (id: string, status: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await medalsService.updateQuoteStatus(id, status, accessToken);
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar status' };
    }
  }, [accessToken, fetchQuotes]);

  const markMedalQuoteAsViewed = useCallback(async (quoteId: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    // Optimistically update local state
    setQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId ? { ...q, isViewed: true } : q
      )
    );

    try {
      await withAuthRetry(
        () => medalsService.markMedalQuoteAsViewed(quoteId, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      // Revert optimistic update on error
      setQuotes((prev) =>
        prev.map((q) =>
          q.id === quoteId ? { ...q, isViewed: false } : q
        )
      );
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao marcar como visualizado' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const confirmCustomItemPrice = useCallback(async (itemId: string, payload: medalsService.ConfirmCustomItemPricePayload) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await medalsService.confirmCustomItemPrice(itemId, payload, accessToken);
      await fetchPendingCustomItems();
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao confirmar preço' };
    }
  }, [accessToken, fetchPendingCustomItems, fetchQuotes]);

  const updateQuoteItem = useCallback(async (itemId: string, data: { quantity: number }) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      const result = await withAuthRetry(
        () => medalsService.updateQuoteItem(itemId, data, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const deleteQuoteItem = useCallback(async (itemId: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await withAuthRetry(
        () => medalsService.deleteQuoteItem(itemId, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao remover item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const addQuoteItem = useCallback(async (quoteId: string, data: any) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      const result = await withAuthRetry(
        () => medalsService.addQuoteItem(quoteId, data, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao adicionar item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  return {
    // Tab
    activeTab,
    setActiveTab,

    // Loading
    loading,
    saving,

    // Data
    categories,
    materials,
    products,
    ribbonTypes,
    ribbonStyles,
    ribbonColors,
    settings,
    quotes,
    setQuotes,
    pendingCustomItems,

    // Filters
    searchTerm,
    setSearchTerm,
    includeInactive,
    setIncludeInactive,

    // Dialog
    dialogOpen,
    setDialogOpen,
    selectedItem,
    setSelectedItem,

    // Fetch
    fetchData,
    fetchCategories,
    fetchMaterials,
    fetchProducts,
    fetchRibbons,
    fetchSettings,
    fetchQuotes,
    fetchPendingCustomItems,

    // CRUD
    createCategory,
    updateCategory,
    deleteCategory,
    createMaterial,
    updateMaterial,
    deleteMaterial,
    createProduct,
    updateProduct,
    deleteProduct,
    toggleProductStatus,
    updateQuoteStatus,
    markMedalQuoteAsViewed,
    confirmCustomItemPrice,
    updateQuoteItem,
    deleteQuoteItem,
    addQuoteItem,
  };
}
